export interface FamilyStarterInput {
  lovedFirstName: string;
  lovedLastName: string;
  dateOfBirth: string;
  dateOfPassing: string;
  plannerName: string;
  relationship: string;
  phone: string;
  email: string;
}

export const FAMILY_DRAFT_STORAGE_KEY = "wehonor.arrangementStudio.familyDraft.v2";
export const FAMILY_SUBMISSION_STORAGE_KEY = "wehonor.arrangementStudio.familySubmission.v1";

export type FamilyStarterField = keyof FamilyStarterInput;
export type FamilyStarterErrors = Partial<Record<FamilyStarterField, string>>;

export type FamilySubmissionStatus =
  | "idle"
  | "submitting"
  | "queued_offline"
  | "submitted"
  | "error";

export interface FamilySubmissionRecord {
  idempotencyKey: string;
  status: "queued_offline" | "submitted";
  submittedAt?: string;
  queuedAt?: string;
}

export function parseFamilySubmissionRecord(raw: string | null): FamilySubmissionRecord | null {
  if (!raw) return null;
  const parsed = JSON.parse(raw) as Partial<FamilySubmissionRecord>;
  if (
    typeof parsed.idempotencyKey !== "string" ||
    !parsed.idempotencyKey.startsWith("family-") ||
    (parsed.status !== "queued_offline" && parsed.status !== "submitted")
  ) {
    throw new Error("Invalid family submission record");
  }
  if (parsed.status === "submitted" && typeof parsed.submittedAt !== "string") {
    throw new Error("Submitted family record is missing its timestamp");
  }
  if (parsed.status === "queued_offline" && typeof parsed.queuedAt !== "string") {
    throw new Error("Queued family record is missing its timestamp");
  }
  return parsed as FamilySubmissionRecord;
}

export type SubmissionBlockReason =
  | "validation"
  | "offline"
  | "submitting"
  | "already_submitted";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const MAX_NAME_LENGTH = 100;
const MAX_RELATIONSHIP_LENGTH = 120;

function requiredText(value: string, label: string, maxLength = MAX_NAME_LENGTH) {
  const normalized = value.trim();
  if (!normalized) return `${label} is required.`;
  if (normalized.length > maxLength) return `${label} must be ${maxLength} characters or fewer.`;
  return undefined;
}

function parseUsDate(value: string): Date | null {
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value.trim());
  if (!match) return null;
  const month = Number(match[1]);
  const day = Number(match[2]);
  const year = Number(match[3]);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  if (
    parsed.getUTCFullYear() !== year ||
    parsed.getUTCMonth() !== month - 1 ||
    parsed.getUTCDate() !== day
  ) {
    return null;
  }
  return parsed;
}

function dateError(value: string, label: string, now: Date, allowUnsure = false) {
  const normalized = value.trim();
  if (!normalized || (allowUnsure && normalized.toLowerCase() === "unsure")) return undefined;
  const parsed = parseUsDate(normalized);
  if (!parsed) return `${label} must use MM/DD/YYYY${allowUnsure ? ' or “unsure”' : ""}.`;
  if (parsed.getTime() > now.getTime()) return `${label} cannot be in the future.`;
  return undefined;
}

export function validateFamilyStarter(
  starter: FamilyStarterInput,
  now = new Date(),
): FamilyStarterErrors {
  const errors: FamilyStarterErrors = {};

  errors.lovedFirstName = requiredText(starter.lovedFirstName, "Loved one first name");
  errors.lovedLastName = requiredText(starter.lovedLastName, "Loved one last name");
  errors.plannerName = requiredText(starter.plannerName, "Your name");
  errors.relationship = requiredText(
    starter.relationship,
    "Relationship",
    MAX_RELATIONSHIP_LENGTH,
  );

  const phone = starter.phone.trim();
  const email = starter.email.trim();
  if (!phone && !email) {
    const message = "Add a phone number or email address so Monarch can follow up.";
    errors.phone = message;
    errors.email = message;
  }

  if (phone) {
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 7 || digits.length > 15) {
      errors.phone = "Enter a phone number with 7 to 15 digits.";
    }
  }

  if (email && !EMAIL_PATTERN.test(email)) {
    errors.email = "Enter an email address such as name@example.com.";
  }

  errors.dateOfBirth = dateError(starter.dateOfBirth, "Date of birth", now);
  errors.dateOfPassing = dateError(starter.dateOfPassing, "Date of passing", now, true);

  const birth = parseUsDate(starter.dateOfBirth);
  const passing = parseUsDate(starter.dateOfPassing);
  if (birth && passing && passing.getTime() < birth.getTime()) {
    errors.dateOfPassing = "Date of passing cannot be before the date of birth.";
  }

  return Object.fromEntries(
    Object.entries(errors).filter(([, value]) => Boolean(value)),
  ) as FamilyStarterErrors;
}

export function familyStarterIsComplete(errors: FamilyStarterErrors) {
  return Object.keys(errors).length === 0;
}

function stableHash(value: string) {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

export function createFamilySubmissionKey(
  starter: FamilyStarterInput,
  answers: Record<string, { selected: string[]; notes: string; needsHelp: boolean }>,
) {
  const normalized = {
    starter: Object.fromEntries(
      Object.entries(starter).map(([key, value]) => [key, value.trim().toLowerCase()]),
    ),
    answers: Object.fromEntries(
      Object.entries(answers)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, answer]) => [
          key,
          {
            selected: [...answer.selected].map((item) => item.trim()).sort(),
            notes: answer.notes.trim(),
            needsHelp: answer.needsHelp,
          },
        ]),
    ),
  };
  return `family-${stableHash(JSON.stringify(normalized))}`;
}

export function getSubmissionBlockReason({
  errors,
  online,
  status,
}: {
  errors: FamilyStarterErrors;
  online: boolean;
  status: FamilySubmissionStatus;
}): SubmissionBlockReason | null {
  if (status === "submitting") return "submitting";
  if (status === "submitted") return "already_submitted";
  if (!familyStarterIsComplete(errors)) return "validation";
  if (!online) return "offline";
  return null;
}

export function submissionBlockMessage(reason: SubmissionBlockReason) {
  switch (reason) {
    case "validation":
      return "Review the highlighted family and contact details before sending.";
    case "offline":
      return "You appear to be offline. This request is saved on this device and can be retried when you reconnect.";
    case "submitting":
      return "This request is already being sent. Please wait for confirmation.";
    case "already_submitted":
      return "This exact arrangement request has already been sent to Monarch.";
  }
}
